// src/components/customer/WalletTopupQRModal.jsx
// Customer-facing QR-to-pay modal for a wallet top-up. Styled to match the
// existing "Buy via PayNow" product modal on the Customer Dashboard
// (products.css .paynow-qr-* classes) so the top-up flow feels familiar.
// Unlike that modal (which asks the customer to email a screenshot), the
// customer never self-confirms payment here — they upload the screenshot
// in-app for admin to verify from the Customer Details Page.
import React, { useState, useRef } from 'react';
import '../../styles/products.css';

const WalletTopupQRModal = ({
  isOpen,
  onClose,
  qrCodeData,
  qrCodeUrl,
  fallback,
  amount,
  isGenerating,
  onUploadProof,
}) => {
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [uploaded, setUploaded] = useState(false);
  const fileInputRef = useRef(null);

  if (!isOpen) return null;

  const handleFileSelected = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    setUploadError('');
    try {
      await onUploadProof(file);
      setUploaded(true);
    } catch (err) {
      console.error('Error uploading top-up proof:', err);
      setUploadError('Failed to upload screenshot. Please try again.');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  return (
    <div className="paynow-qr-overlay" onClick={() => !uploading && onClose()}>
      <div className="paynow-qr-modal" onClick={(e) => e.stopPropagation()}>
        <button className="paynow-qr-close" onClick={onClose} aria-label="Close">✕</button>

        {uploaded ? (
          <>
            <h3>Screenshot uploaded!</h3>
            <p className="paynow-product-info">
              Our team will review your payment and credit your wallet shortly.
              We'll notify you once it's verified.
            </p>
            <button className="paynow-qr-close-btn" onClick={onClose}>Close</button>
          </>
        ) : (
          <>
            <h3>Top Up Wallet</h3>
            <p className="paynow-product-info">${parseFloat(amount || 0).toFixed(2)}</p>

            {isGenerating ? (
              <p className="paynow-qr-instructions">Generating QR code...</p>
            ) : qrCodeData ? (
              <img src={`data:image/png;base64,${qrCodeData}`} alt="PayNow QR Code" />
            ) : qrCodeUrl ? (
              <img src={qrCodeUrl} alt="PayNow QR Code" />
            ) : (
              <p className="paynow-qr-unavailable">PayNow QR code not available. Please contact the salon.</p>
            )}

            <p className="paynow-qr-instructions">Scan this QR code with your banking app to pay.</p>

            {fallback && (
              <p className="paynow-qr-no-config">
                Please enter the amount (${parseFloat(amount || 0).toFixed(2)}) manually when scanning this QR code.
              </p>
            )}

            <p className="paynow-qr-screenshot-note">
              After paying, upload a screenshot of your payment confirmation below.
              Our team will verify it and credit your wallet — you'll be notified once it's done.
            </p>

            {uploadError && <p className="paynow-qr-unavailable">{uploadError}</p>}

            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={handleFileSelected}
            />
            <button
              className="paynow-qr-close-btn"
              disabled={uploading || isGenerating}
              onClick={() => fileInputRef.current?.click()}
            >
              {uploading ? 'Uploading...' : 'Upload Payment Screenshot'}
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default WalletTopupQRModal;
